import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

const COLORS = [
  '#8b5cf6', '#6366f1', '#3b82f6', '#06b6d4', 
  '#10b981', '#f59e0b', '#ec4899', '#f43f5e'
]

function createBubble(id, containerWidth, containerHeight) {
  const size = 30 + Math.random() * 40 // 30-70px
  return {
    id,
    x: Math.random() * (containerWidth - size),
    y: containerHeight + size, // Start below the container
    size,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    speed: 0.5 + Math.random() * 1, // 0.5-1.5 px per frame
    wobble: Math.random() * Math.PI * 2, // Random start phase
    wobbleSpeed: 0.02 + Math.random() * 0.03,
    wobbleAmount: 10 + Math.random() * 20,
  }
}

export default function BubblePop() {
  const [bubbles, setBubbles] = useState([])
  const [score, setScore] = useState(0)
  const [popped, setPopped] = useState([]) // For pop animation
  const containerRef = useRef(null)
  const nextIdRef = useRef(0)
  const animationRef = useRef(null)

  const containerWidth = 280
  const containerHeight = 320

  // Initialize bubbles
  const initBubbles = useCallback(() => {
    const initial = []
    for (let i = 0; i < 8; i++) {
      initial.push(createBubble(nextIdRef.current++, containerWidth, containerHeight))
    }
    setBubbles(initial)
    setScore(0)
    setPopped([])
  }, [])

  useEffect(() => {
    initBubbles()
  }, [initBubbles])

  // Animation loop
  useEffect(() => {
    const animate = () => {
      setBubbles(prev => {
        const updated = prev.map(bubble => ({
          ...bubble,
          y: bubble.y - bubble.speed,
          wobble: bubble.wobble + bubble.wobbleSpeed,
          x: bubble.x + Math.sin(bubble.wobble) * 0.5,
        })).filter(bubble => bubble.y > -bubble.size) // Remove bubbles that went off screen

        // Add new bubbles to maintain count
        while (updated.length < 8) {
          updated.push(createBubble(nextIdRef.current++, containerWidth, containerHeight))
        }

        return updated
      })

      // Clean up old pop animations
      setPopped(prev => prev.filter(p => Date.now() - p.time < 300))

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  const handlePop = (bubble, e) => {
    e.stopPropagation()
    
    // Add pop animation
    setPopped(prev => [...prev, { 
      id: bubble.id, 
      x: bubble.x + bubble.size / 2, 
      y: bubble.y + bubble.size / 2,
      time: Date.now() 
    }])
    
    // Remove bubble
    setBubbles(prev => prev.filter(b => b.id !== bubble.id))
    
    // Increase score
    setScore(prev => prev + 1)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
          Bubble Pop
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
            Score: {score}
          </Typography>
          <IconButton
            onClick={initBubbles}
            size="small"
            sx={{ color: '#6b7280' }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Game area */}
      <Box
        ref={containerRef}
        sx={{
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
          background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Bubbles */}
        {bubbles.map(bubble => (
          <Box
            key={bubble.id}
            onClick={(e) => handlePop(bubble, e)}
            sx={{
              position: 'absolute',
              left: `${bubble.x}px`,
              top: `${bubble.y}px`,
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${bubble.color}99, ${bubble.color})`,
              boxShadow: `0 4px 12px ${bubble.color}40, inset 0 -2px 4px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.5)`,
              cursor: 'pointer',
              transition: 'transform 0.1s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
              '&:active': {
                transform: 'scale(0.9)',
              },
              // Shine effect
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '15%',
                left: '20%',
                width: '30%',
                height: '30%',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          />
        ))}

        {/* Pop animations */}
        {popped.map(pop => (
          <Box
            key={`pop-${pop.id}`}
            sx={{
              position: 'absolute',
              left: `${pop.x}px`,
              top: `${pop.y}px`,
              transform: 'translate(-50%, -50%)',
              fontSize: '24px',
              animation: 'popAnimation 0.3s ease-out forwards',
              '@keyframes popAnimation': {
                '0%': {
                  opacity: 1,
                  transform: 'translate(-50%, -50%) scale(1)',
                },
                '100%': {
                  opacity: 0,
                  transform: 'translate(-50%, -50%) scale(1.5)',
                },
              },
            }}
          >
            ✨
          </Box>
        ))}

        {/* Instructions overlay (shown briefly) */}
        {score === 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '8px',
              padding: '8px 16px',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography sx={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
              Tap the bubbles!
            </Typography>
          </Box>
        )}
      </Box>

      {/* High score hint */}
      <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
        Pop as many bubbles as you can while waiting
      </Typography>
    </Box>
  )
}
